import path from "node:path";
import axios from "axios";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import type { Attachment } from "@/API";
import { AttachmentFactory } from "@/model";
import sentry from "@/sentry";
import { ServerApiAccess } from "@/server-api-access";
import { useAppStore, useFileStore } from "@/store";
import type { FileLocalUrl, FileParam, SetFiles } from "@/store/file-store";
import { startBackgroundUpload, type UploadTarget } from "./direct-uploader";
import { getFileType } from "./file-utility";

/**
 * S3へのデータアクセスクラス
 * ファイルはS3の /domainId/topicId/messageId[/commentId]/ に保存される
 */
export default class S3AccessUtility {
  private static instance: S3AccessUtility;

  /* 現在設定されている組織ID。組織変更じに再設定されます */
  private domainId: string = "";

  /* 現在設定されている話題ID。話題変更時に再設定されます */
  private topicId: string = "";

  public static getInstance(): S3AccessUtility {
    if (!S3AccessUtility.instance) {
      S3AccessUtility.instance = new S3AccessUtility();
      S3AccessUtility.instance.init();
    }
    return S3AccessUtility.instance;
  }

  public static encodeName(fileName: string): string {
    return fileName.replace(/\./, `#${uuidv4()}.`);
  }

  public static decodeName(encode: string): string {
    const uuiodReg =
      /#([0-9a-fA-F]{8}[-][0-9a-fA-F]{4}[-][0-9a-fA-F]{4}[-][0-9a-fA-F]{4}[-][0-9a-fA-F]{12})/;
    const match = encode.match(uuiodReg);
    if (!match || match.length < 1) {
      // マッチしない場合はそのままのファイル名を返す
      return encode;
    }
    const uuid = match[1];
    return encode.replace(`#${uuid}`, "");
  }

  public init(): void {
    this.domainId = "";
    this.topicId = "";
  }

  private isPdfOrMovie = (file: File | Attachment): boolean => {
    const type = getFileType(file);
    switch (type) {
      case "pdf":
      case "video":
        return true;
      default:
        return false;
    }
  };

  private isAndroidDevice(): boolean {
    return useAppStore().isAndroid;
  }

  public setDomainId(domainId: string): void {
    this.domainId = domainId;
  }

  public setTopicId(topicId: string): void {
    this.topicId = topicId;
  }

  /**
   * ファイルのアップロード (generator)
   * 1つファイルがアップロード完了する度に、
   * 完了したファイル数、アップロードするファイル数、全ての添付ファイル、処理の終了フラグ
   * を返す
   * @param files
   * @param param
   * @returns
   */
  public async *uploadFilesGenerator(
    files: (File | Attachment)[],
    param: UploadTarget,
  ) {
    try {
      const fileParams: {
        messageId?: string;
        commentId?: string;
        fileName: string;
        contentType: string;
        size: number;
      }[] = [];
      // 新規追加されるファイルはファイル名をエンコードした新しいFileObjectへ置き換える
      const encodedFiles: (File | Attachment)[] = files.map((file) => {
        if (!AttachmentFactory.isAttachment(file)) {
          const encodeName = S3AccessUtility.encodeName(file.name);
          fileParams.push({
            messageId: param.messageId,
            commentId: param.commentId,
            fileName: encodeName,
            contentType: file.type,
            size: file.size,
          });
          const encodedFile = new File([file], encodeName, { type: file.type });
          return encodedFile;
        } else {
          return file;
        }
      });

      const access = new ServerApiAccess();
      const results = await access.putFiles(
        param.domainId,
        param.topicId,
        fileParams,
      );
      if (!results) return { uploaded: 0, total: 0, done: true };
      let uploaded: number = 0;
      const fileData = results.data;
      const total: number = fileData.length;
      const attachments: Attachment[] = [];
      yield { uploaded, total, attachments, done: false };
      // 正規表現用にメタ文字をエスケープする
      const escapeRegExp = (string: string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      };
      for (const file of encodedFiles) {
        if (!AttachmentFactory.isAttachment(file)) {
          const result = fileData.find(
            (_res) => !!_res.key.match(escapeRegExp(file.name)),
          );
          if (result) {
            // direct アプリではネイティブにアップロードさせ、完了は待たない（dxflow-3331）
            if (
              !startBackgroundUpload(
                result.key,
                result.signedUrl,
                file,
                S3AccessUtility.decodeName(file.name),
                param,
              )
            ) {
              await axios.put(result.signedUrl, file, {
                headers: { "Content-Type": file.type },
              });
            }

            // PDFや動画ファイルの場合はサムネイルをアップロード
            if (this.isPdfOrMovie(file) && results.thumb.length) {
              const thumb = results.thumb.find(
                (_res) => !!_res.key.match(escapeRegExp(file.name)),
              );
              if (thumb) {
                const storeThumb = useFileStore().getThumb(file) as File;
                if (storeThumb) {
                  await axios.put(thumb.signedUrl, storeThumb, {
                    headers: { "Content-Type": storeThumb.type },
                  });
                }
              }
            }
            uploaded++;
            const attachment = AttachmentFactory.createFromFile(file);
            attachments.push(attachment);
            yield { uploaded, total, attachments, done: false };
          } else {
            yield { uploaded, total, attachments, done: false };
          }
        } else {
          attachments.push(file);
          yield { uploaded, total, attachments, done: false };
        }
      }
      return { uploaded, total, attachments, done: true };
    } catch (err) {
      sentry.sendSentryError({ api: "deleteFiles", err });
    }
  }

  /**
   * s3の署名付きURLの有効期限を取得
   * @param url presigned URL
   * @returns
   */
  private getPresignedUrlExpireTime(url: string): moment.Moment | null {
    const params = new URL(url).searchParams;
    const amzDate = params.get("X-Amz-Date");
    const amzExpires = parseInt(params.get("X-Amz-Expires") || "", 10); // server側では 900

    if (!amzDate || Number.isNaN(amzExpires)) return null;

    // momentでUTC時刻に変換
    // amzDate: YYYYMMDDTHHmmssZ
    const baseMoment = moment.utc(amzDate, "YYYYMMDD'T'HHmmss'Z'");
    if (!baseMoment.isValid()) return null;
    const expireMoment = baseMoment.clone().add(amzExpires, "seconds");
    return expireMoment;
  }

  /**
   * URLの確認と取得
   * @param url
   * @param param
   * @param fileName
   * @param isThumb
   * @param contentType
   * @returns
   */
  private async confirmFileUrl(
    url: string,
    param: { topicId?: string; messageId: string; commentId: string },
    fileName: string,
    isThumb: boolean,
    contentType: string,
  ) {
    let resultUrl = "";
    try {
      if (!url) {
        // URL取得へ
        const result = await this.retrySignedUrl(
          param,
          fileName,
          isThumb,
          contentType,
        );
        if (result) resultUrl = result.url;
        else {
          resultUrl = "";
        }
      } else {
        resultUrl = url;
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        // 403エラーの時は再取得に
        const result = await this.retrySignedUrl(
          param,
          fileName,
          isThumb,
          contentType,
        );
        if (result) resultUrl = result.url;
        else {
          resultUrl = "";
        }
      } else {
        resultUrl = "";
      }
    }
    return resultUrl;
  }

  /**
   * ファイルの署名付きURLの再取得
   * @param param
   * @param fileName
   * @param isThumb
   * @param contentType
   * @returns ローカルURL
   */
  private async retrySignedUrl(
    param: { topicId?: string; messageId: string; commentId: string },
    fileName: string,
    isThumb: boolean,
    contentType: string,
  ): Promise<FileLocalUrl | undefined> {
    try {
      const domainId = useAppStore().domainId;
      const topicId = useAppStore().topicId;
      const access = new ServerApiAccess();
      const pathParam = { domainId: domainId, topicId: topicId, ...param };
      const result = await access.getFile(
        pathParam,
        fileName,
        isThumb,
        contentType,
      );
      if (result) {
        if (isThumb) {
          const setFiles = (await useFileStore().set({
            thumb: [result],
          })) as SetFiles;
          return setFiles.thumb![0];
        } else {
          const setFiles = (await useFileStore().set({
            data: [result],
          })) as SetFiles;
          return setFiles.data![0];
        }
      } else {
        const key = `${domainId}/${topicId}/${param.messageId ? `${param.messageId}/` : ""}${param.commentId ? `${param.commentId}/` : ""}${fileName}`;
        if (isThumb) {
          useFileStore().set({ thumb: [{ key: key, signedUrl: "" }] });
        } else {
          useFileStore().set({ data: [{ key: key, signedUrl: "" }] });
        }
        return undefined;
      }
    } catch (_err) {
      return undefined;
    }
  }

  /**
   * GET: ファイルをS3から取得
   * @param fileName
   * @param param
   */
  public async getFile(
    fileName: string,
    param: { topicId?: string; messageId: string; commentId: string },
    contentType: string,
  ): Promise<unknown | string> {
    fileName = path.basename(fileName); // 余計なパラメータ類を削除
    const topicId = param.topicId || this.topicId;
    const isThumb = false;
    if (param.messageId && !param.commentId) {
      // 投稿
      const messageFiles = useFileStore().getMessageFiles(
        this.domainId,
        topicId,
      ) as FileParam[];
      if (!messageFiles) return "";
      const messageFile = messageFiles.find((file: FileParam) => {
        return file.fileName === fileName && file.messageId === param.messageId;
      });
      const fileData = await this.confirmFileUrl(
        messageFile?.data || "",
        param,
        fileName,
        isThumb,
        contentType,
      );
      return fileData;
    } else if (param.messageId && param.commentId) {
      // コメント
      const commentFiles = useFileStore().getCommentFilesByTopicId(
        this.domainId,
        topicId,
      ) as FileParam[];
      if (!commentFiles) return "";
      const commentFile = commentFiles.find((file: FileParam) => {
        return (
          file.fileName === fileName &&
          file.messageId === param.messageId &&
          file.commentId === param.commentId
        );
      });
      const fileData = await this.confirmFileUrl(
        commentFile?.data || "",
        param,
        fileName,
        isThumb,
        contentType,
      );
      return fileData;
    } else {
      console.log(
        `*Invalid Path. reads/${this.domainId}/${topicId}/${param.messageId}/${param.commentId}/ ファイル名: ${fileName}`,
      );
      return "";
    }
  }

  /**
   * GET: ファイルをS3から取得
   * @param fileName
   * @param param
   */
  public async getThumbFile(
    fileName: string,
    param: { topicId?: string; messageId: string; commentId: string },
    contentType: string,
  ): Promise<unknown | string> {
    fileName = path.basename(fileName); // 余計なパラメータ類を削除
    const topicId = param.topicId || this.topicId;
    const isThumb = true;
    if (param.messageId && !param.commentId) {
      // 投稿
      const messageFiles = useFileStore().getMessageFiles(
        this.domainId,
        topicId,
      ) as FileParam[];
      if (!messageFiles) return "";
      const messageFile = messageFiles.find((file: FileParam) => {
        return file.fileName === fileName && file.messageId === param.messageId;
      });
      const thumb = await this.confirmFileUrl(
        messageFile?.thumb || "",
        param,
        fileName,
        isThumb,
        contentType,
      );
      return thumb;
    } else if (param.messageId && param.commentId) {
      // コメント
      const commentFiles = useFileStore().getCommentFilesByTopicId(
        this.domainId,
        topicId,
      ) as FileParam[];
      if (!commentFiles) return "";
      const commentFile = commentFiles.find((file: FileParam) => {
        return (
          file.fileName === fileName &&
          file.messageId === param.messageId &&
          file.commentId === param.commentId
        );
      });
      const thumb = await this.confirmFileUrl(
        commentFile?.thumb || "",
        param,
        fileName,
        isThumb,
        contentType,
      );
      return thumb;
    } else if (
      topicId !== "" &&
      param.messageId === "" &&
      param.commentId === ""
    ) {
      // 話題
      const topicsFiles = useFileStore().getTopicFiles(
        this.domainId,
      ) as FileParam[];
      if (!topicsFiles) return "";
      const topicFile = topicsFiles.find((file: FileParam) => {
        return file.fileName === fileName && file.topicId && param.topicId;
      });
      const thumb = await this.confirmFileUrl(
        topicFile?.thumb || "",
        param,
        fileName,
        isThumb,
        contentType,
      );
      return thumb;
    } else {
      const rootPath = "thumb";
      console.log(
        `*Invalid Path. ${rootPath}/${this.domainId}/${topicId}/${param.messageId}/${param.commentId}/ ファイル名: ${fileName}`,
      );
      return fileName;
    }
  }

  /**
   * ファイル削除
   * @param files
   * @param param
   * @returns
   */
  public async deleteFiles(
    files: Attachment[],
    param: {
      domainId: string;
      topicId: string;
      messageId?: string;
      commentId?: string;
    },
  ): Promise<void> {
    try {
      const fileParams: {
        messageId?: string;
        commentId?: string;
        fileName: string;
      }[] = [];
      files.forEach((file) => {
        if (AttachmentFactory.isAttachment(file)) {
          fileParams.push({
            messageId: param.messageId,
            commentId: param.commentId,
            fileName: file.url,
          });
        }
      });

      const access = new ServerApiAccess();
      const results = await access.deleteFiles(
        param.domainId,
        param.topicId,
        fileParams,
      );
      if (!results) {
        return;
      }
      const data = results.data;
      await Promise.all(
        data.map(async (result) => {
          await axios.delete(result.signedUrl);
        }),
      );

      // サムネの削除
      const thumb = results.thumb;
      await Promise.all(
        thumb.map(async (result) => {
          await axios.delete(result.signedUrl);
        }),
      );
    } catch (err) {
      sentry.sendSentryError({ api: "deleteFiles", err });
    }
  }

  /**
   * GET+DOWNLOAD: S3から取得したファイルのダウンロードを実行
   * @param fileName
   * @param param
   * @param downloadName // ダウンロード時のファイル名
   */
  public async downloadFile(
    fileName: string,
    param: { topicId?: string; messageId: string; commentId: string },
    contentType: string,
    downloadName?: string,
  ): Promise<void> {
    try {
      fileName = path.basename(fileName); // 余計なパラメータ類を削除
      const domainId = useAppStore().domainId;
      const topicId = useAppStore().topicId;
      const access = new ServerApiAccess();
      const pathParam = { domainId: domainId, topicId: topicId, ...param };
      const result = await access.getFile(
        pathParam,
        fileName,
        false,
        contentType,
      );
      if (!result) {
        return;
      }
      const signedUrl = result.signedUrl;
      const response = await axios.get(signedUrl, { responseType: "blob" });

      if (this.isAndroidDevice()) {
        // Androidのみ download handler でメモリ解放
        const eventListener = (event: any) => {
          if (event.url === url) {
            URL.revokeObjectURL(url);
            window.removeEventListener(
              "directjsapidownloadfinished",
              eventListener,
            );
          }
        };
        window.addEventListener("directjsapidownloadfinished", eventListener);
      }

      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName ?? fileName;
      a.click();

      if (!this.isAndroidDevice()) {
        // Android以外は即メモリ解放
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      sentry.sendSentryError({ api: "downloadFiles", err });
    }
  }
}
